<template>
  <div class="external-bulk-order-form">
    <div
      v-if="isPending"
      class="d-flex justify-content-center align-items-center choose-tenant-loading"
    >
      <div class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t("Loading...") }}</span>
        </div>
        <p class="mt-3 text-muted">{{ $t("Loading") }}</p>
      </div>
    </div>

    <div v-else class="obiado-form-inner animated fadeIn">
      <div class="d-flex justify-content-end pt-3">
        <b-button class="logout-btn" size="sm" variant="outline-secondary" @click="logout">
          <i class="fas fa-sign-out-alt me-1"></i>
          {{ t("Logout") }}
        </b-button>
      </div>

      <div class="obiado-hero">
        <img :alt="logoAlt" :src="logo" class="obiado-hero__logo" />
        <h1 class="obiado-hero__title">{{ $t("Choose tenant to which you want to login.") }}</h1>
      </div>

      <div class="obiado-card">
        <b-input-group class="mb-3">
          <div class="input-group-text">
            <i class="fa fa-search" />
          </div>
          <b-form-input
            v-model="search"
            :placeholder="$t('Search tenant')"
            autofocus
            size="lg"
            type="search"
          />
        </b-input-group>

        <p v-if="displayedTenants.length === 0" class="choose-tenant-empty">
          {{ $t("No tenants match your search.") }}
        </p>

        <div v-else class="choose-tenant-grid">
          <a
            v-for="tenant in displayedTenants"
            :key="tenant.id"
            :href="fillUrlTemplate(tenant.slug)"
            :title="$t('Login')"
            class="choose-tenant-card text-decoration-none"
          >
            <i class="fa fa-university choose-tenant-card__icon" />
            <span class="choose-tenant-card__slug">{{ tenant.slug }}</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { fillUrlTemplate } from "@/lib/redirectHelper.ts";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useCurrentGlobalUserQuery } from "@/modules/auth/queries/useCurrentGlobalUserQuery.ts";

const store = useStore();
const { push } = useRouter();
const { t } = useI18n();
const logo = computed(() => "/img/obiado.svg");
const logoAlt = computed(() => "Obiado logo");

const logout = async () => {
  try {
    await store.dispatch("auth/logout");
    await push({ name: "auth.login" });
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

const { isPending, data } = useCurrentGlobalUserQuery();

const search = ref("");

const displayedTenants = computed(() => {
  const tenants = data.value?.tenants ?? [];
  const sorted = tenants.slice().sort((a, b) => a.slug.localeCompare(b.slug));
  const q = search.value.trim().toLowerCase();

  if (q === "") {
    return sorted;
  }

  return sorted.filter((t) => t.slug.toLowerCase().includes(q));
});
</script>

<style lang="scss">
.choose-tenant-loading {
  min-height: 100vh;
}

.choose-tenant-empty {
  text-align: center;
  color: var(--obiado-muted);
  padding: 12px 0 4px;
  margin-bottom: 0;
}

.choose-tenant-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 601px) and (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.choose-tenant-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px 12px;
  background: var(--obiado-surface);
  border: 1px solid var(--obiado-border);
  border-radius: var(--obiado-radius-field);
  color: var(--obiado-text);
  text-align: center;
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    color 0.15s;

  &:hover {
    border-color: var(--obiado-brand);
    box-shadow: 0 0 0 3px rgba(237, 90, 34, 0.1);
    color: var(--obiado-brand-strong);
  }
}

.choose-tenant-card__icon {
  font-size: 22px;
  color: var(--obiado-brand);
  flex-shrink: 0;
}

.choose-tenant-card__slug {
  font-size: var(--obiado-fs-body);
  font-weight: 600;
  word-break: break-all;
}
</style>
